import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Spinner,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@monksflow/monks-ui';

import { DeleteConfirmationModalProps } from './DeleteConfirmationModal.types';
import { useTranslation } from 'react-i18next';

export const DeleteConfirmationModal = ({
  open,
  isDeleting = false,
  onCancel,
  onConfirmDelete,
  resourceType,
  resourceName,
  customModalTitle,
  warningMessage,
  confirmLabel,
  pendingLabel,
  'data-testid': dataTestId,
}: DeleteConfirmationModalProps) => {
  const { t } = useTranslation('common');

  const modalTitle =
    customModalTitle ??
    (resourceName
      ? t('components.deleteConfirmationModal.deleteResourceName', { resourceName })
      : t('components.deleteConfirmationModal.deleteResourceType', {
          resourceType: resourceType ?? '',
        }));

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && !isDeleting) {
      onCancel();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="gap-0 p-4 max-w-120"
        showCloseButton={!isDeleting}
        data-testid={dataTestId}
        onEscapeKeyDown={(event) => {
          if (isDeleting) event.preventDefault();
        }}
        onPointerDownOutside={(event) => {
          if (isDeleting) event.preventDefault();
        }}
        onInteractOutside={(event) => {
          if (isDeleting) event.preventDefault();
        }}
      >
        <DialogHeader className="overflow-hidden">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <DialogTitle className="min-w-0 truncate pr-6">{modalTitle}</DialogTitle>
              </TooltipTrigger>
              <TooltipContent className="max-w-[300px] wrap-break-word">
                {resourceName}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </DialogHeader>
        <DialogBody className="flex flex-col gap-4 pb-10 pt-4">
          <p className="text-foreground-alt">{warningMessage}</p>
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancel} disabled={isDeleting}>
            {t('components.deleteConfirmationModal.cancel')}
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={onConfirmDelete}
            disabled={isDeleting}
          >
            {isDeleting && <Spinner />}
            {isDeleting
              ? (pendingLabel ?? t('components.deleteConfirmationModal.deleting'))
              : (confirmLabel ?? t('components.deleteConfirmationModal.yesIWantToDelete'))}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

DeleteConfirmationModal.displayName = 'DeleteConfirmationModal';
